import { Injectable } from '@angular/core';

import { User } from '../../models/user';

import { HttpClientService } from '../common/http-client.service';

declare var $: any;
declare var window: any;

@Injectable()
export class AuthService {
  /**
   * URL for auth requests
   * @type {string}
   */
  private usersURL: string = 'users';

  /**
   * Groups list of current user
   * @type {Array<string>}
   */
  public userGroups: Array<string>;

  /**
   * App env
   * @type {string}
   */
  private env: string;

  /**
   * Current user id
   * @type {number}
   */
  public current_user_id: number;

  /**
   * Current user info
   */
  private user: User = new User({});

  /**
   * Promise which resolves into user info
   * @type {Promise<any>}
   */
  private userGroupsPromise: Promise<any>;

  /**
   * Indicates if app env is in development mode
   * @type {boolean}
   */
  isDevelopmentEnv: boolean = false;

  /**
   * Indicates if app env is in production mode
   * @type {boolean}
   */
  isProductionEnv: boolean = false;

  /**
   * Constructor
   * @param {HttpClientService} private httpClient Service with base functions to call different http methods
   */
  constructor(private httpClient: HttpClientService) {
    this.getCurrentUserGroups();
  }

  /**
   * Return boolean value indicating if application environment is development
   * @return {boolean} Indicates if application environment is development
   */
  developmentEnv(): boolean {
    return this.env == 'development';
  }

  /**
   * Fetch current user groups, locally if available or from backend
   * @return {Promise<any>} Response with current u
   */
  fetchUserGroups(): Promise<any> {
    if (this.userGroups)
      return new Promise((resolve, reject) => {
        resolve({
          groups: this.userGroups,
          env: this.env,
        });
      });
    else return this.getCurrentUserGroups();
  }

  /**
   * Fetch current user id, locally if available or from backend
   * @return {Promise<any>} [description]
   */
  fetchUserId(): Promise<any> {
    if (this.current_user_id)
      return new Promise((resolve, reject) => {
        resolve({
          current_user_id: this.current_user_id,
          env: this.env,
        });
      });
    else return this.getCurrentUserGroups();
  }

  /**
   * Get current user groups
   * @param  {boolean   = false} reload Force reload of groups from backend, else returns local version otherwise
   * @return {Promise<any>}      Server response with groups, if already fetched, existing promise is returned
   */
  getCurrentUserGroups(reload: boolean = false): Promise<any> {
    if (!reload && this.userGroupsPromise) return this.userGroupsPromise;
    let scope: AuthService = this;
    this.userGroupsPromise = this.httpClient.get(`${this.usersURL}.json?get_groups=true`).then((response) => {
      let response_json = response;
      if (!response_json) {
        console.log('Bad auth result, loading auth will be attempted again in 20 seconds');
        response_json = [];
        window.setTimeout(() => {
          if (scope && scope.getCurrentUserGroups) scope.getCurrentUserGroups(true);
        }, 20000);
        throw `Auth result not returned, response JSON is undefined`;
      }
      this.userGroups = response_json.groups;
      this.env = response_json.env;
      this.current_user_id = response_json.current_user_id;
      this.user = new User({
        groupNames: response_json.groups,
        id: response_json.current_user_id,
        intranet_id: response_json.intranet_id,
      });
      this.isDevelopmentEnv = this.env == 'development';
      this.isProductionEnv = this.env == 'production';
      return response_json;
    });
    return this.userGroupsPromise;
  }

  /**
   * Get current user groups
   * @return {Array<string>} List of current user groups
   */
  getGroups(): Array<string> {
    return this.userGroups;
  }

  /**
   * Get environment of application
   * @return {string} Application environment
   */
  getEnv(): string {
    return this.env;
  }

  /**
   * Get current user info
   * @return {User} User info
   */
  getUser(): User {
    return this.user;
  }

  /**
   * Perform logout for current user
   * @return {Promise<any>} Server response indicating success of logout
   */
  logout(earlyReason: string = '', lunch: boolean = false): Promise<any> {
    // Call logout endpoint BEFORE clearing local auth (so token is included in request).
    // The server may return an early-departure reason request without signing out.
    const params: any = earlyReason ? { early_reason: earlyReason } : {};
    if (lunch) params.lunch = true;
    return this.httpClient.delete('/users/logout.json', params)
      .then((response_json) => {
        if (response_json && response_json.reason_required) return response_json;
        this.clearLocalAuth();
        this.redirectToLogin();
        return response_json;
      })
      .catch((error) => {
        // Even if request fails, clear local auth and redirect
        console.warn('Logout request failed:', error);
        this.clearLocalAuth();
        this.redirectToLogin();
        return { success: true };
      });
  }

  /**
   * Clear local authentication state (tokens, cookies, etc.)
   */
  private clearLocalAuth(): void {
    window.localStorage.removeItem('token');
    window.sessionStorage.removeItem('intranetId');
    window.localStorage.removeItem('intranetId');
  }

  /**
   * Redirect to login page
   */
  private redirectToLogin(): void {
    if (window && window.location && window.location.pathname && window.location.pathname == '/') {
      window.location.reload();
    } else {
      window.location.pathname = '/';
    }
  }

  /**
   * Return boolean value indicating if application environment is production
   * @return {boolean} Indicates if application environment is production
   */
  productionEnv(): boolean {
    return this.env == 'production';
  }

  /**
   * Check if current user can be shown tech debug info in pages
   * @return {Promise<boolean>} Promise containing boolean response indicating if tech debug info can be shown
   */
  techDebug(): Promise<boolean> {
    return this.getCurrentUserGroups().then((response_json) => {
      if (response_json.groups)
        if (response_json.groups.indexOf('admin') > -1 && response_json.groups.indexOf('tech') > -1) return true;
        else return false;
      else return false;
    });
  }

  /**
   * Get authenticated download URL with JWT token
   * @param {string} url The base URL path for download
   * @return {string} URL with token parameter appended
   */
  getAuthenticatedDownloadUrl(url: string): string {
    const token = window.localStorage.getItem('token');
    if (token && token !== 'null' && token !== 'undefined') {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}token=${token}`;
    }
    return url;
  }
}
